import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import Checkbox from 'shared/components/andtComponents/Checkbox';
import Spinner from 'shared/components/andtComponents/Spinner';
import PermissionsExplanation from './PermissionsExplaination';
import { PERMISSION_ACTIONS, PERMISSION_ENTITIES, PERMISSION_TYPES, PERMISSIONS_CATEGORIES } from '../../consts';
import Permission from './Permission';
import useRoles from 'users/new-user-management/hooks/reactQuery/useRoles';
import InfoPopover from 'shared/components/andtComponents/InfoPopover/index';
import { useRootStore } from 'app/contexts/RootStoreContext';
import ActionForAllCategoriesPermissions from 'users/containers/Organization/components/EffectivePermissions/ActionForAllCategoriesPermissions';
import { OrganizationLearnMoreLinks } from 'app/contexts/Brand/configurations/external-links.configuration';
import Button from 'shared/components/andtComponents/Button';
import PermissionsCategorySelection from 'users/containers/Organization/components/EffectivePermissions/PermissionsCategorySelection';

import styles from './EffectivePermissions.module.scss';

const EffectivePermissions = ({
  isViewOnly,
  permissions = [],
  roleId,
  userId,
  isCreateEditMode,
  permissionChanged,
  actionPermissionForAllCategoriesChanged,
}) => {
  const [showGrantedPermissions, setShowGrantedPermissions] = useState(isCreateEditMode ?? true);
  const [selectedCategory, setSelectedCategory] = useState(PERMISSIONS_CATEGORIES.ALL);

  const { usersStore } = useRootStore();

  const { fetchAvailableCategoryActions } = useRoles();
  const { data: availableCategoryActions = [], isLoading } = fetchAvailableCategoryActions(
    usersStore?.isCurrentUserReseller,
  );

  const allCategoriesPermissions = useMemo(() => {
    if (Array.isArray(availableCategoryActions)) {
      const availableCategoryActionsPermissions = availableCategoryActions.map((item) => {
        return {
          category: item.category,
          actionPermissions: item.actions.map((action) => ({
            action: action,
            permissions: [],
          })),
        };
      });
      const tmpPermissionsToDisplay = availableCategoryActionsPermissions.map((item) => {
        const found = permissions.find((obj) => obj.category === item.category);
        return {
          ...item,
          actionPermissions: found?.actionPermissions ? [...found.actionPermissions] : [...item.actionPermissions],
        };
      });
      return tmpPermissionsToDisplay;
    }
  }, [availableCategoryActions, permissions]);

  const permissionsToDisplay = useMemo(() => {
    if (Array.isArray(allCategoriesPermissions) && Array.isArray(permissions)) {
      let tmpPermissionsToDisplay;
      if (showGrantedPermissions) {
        tmpPermissionsToDisplay = permissions.filter((p) =>
          p.actionPermissions.some((ap) => ap.permissions.some((p) => p.permissionType !== PERMISSION_TYPES.NONE.id)),
        );
      } else {
        tmpPermissionsToDisplay = cloneDeep(allCategoriesPermissions);
      }
      if (selectedCategory.id !== PERMISSIONS_CATEGORIES.ALL.id) {
        tmpPermissionsToDisplay = tmpPermissionsToDisplay.filter(
          (p) => PERMISSION_ENTITIES[p.category].permissionsCategory.id === selectedCategory.id,
        );
      }
      return tmpPermissionsToDisplay?.sort((a, b) => {
        const aTitle = PERMISSION_ENTITIES[a.category]?.title || a.category || '';
        const bTitle = PERMISSION_ENTITIES[b.category]?.title || b.category || '';
        return aTitle.localeCompare(bTitle);
      });
    }
  }, [allCategoriesPermissions, permissions, showGrantedPermissions, selectedCategory]);

  const getInfoTooltip = (actionId) => {
    if (actionId === PERMISSION_ACTIONS.IMPERSONATE.id) {
      return 'Allow users to impersonate other users for support purposes. Use sparingly.';
    } else if (actionId === PERMISSION_ACTIONS.EXEC_OFFLINE_TASKS_AS.id) {
      return 'Allow users to determine under which role automatic tasks, such as reports and alert are run, thus determining their data access scope.';
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <div className={styles.effectivePermissionsContainer}>
      <div className={styles.container}>
        <div className={styles.permissionsContainer}>
          <div className={styles.permissionsHeader}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>{isCreateEditMode ? 'Permissions' : 'Effective Permissions'}</div>
              <Checkbox
                isChecked={showGrantedPermissions}
                onChange={() => {
                  setShowGrantedPermissions(!showGrantedPermissions);
                }}
                primary
                text="Show granted permissions"
              />
            </div>
            <div className={styles.rightSideHeader}>
              <Button
                className={{ button: styles.learnMoreButton }}
                isTextButton
                onClick={() => window.open(OrganizationLearnMoreLinks.Permissions, '_blank')}
                text="Learn More"
              />
              <PermissionsCategorySelection
                selectedCategory={selectedCategory}
                onCategorySelectionChanged={(category) => setSelectedCategory(category)}
              />
            </div>
          </div>
          <div className={styles.permissionsGrid}>
            <div className={classNames(styles.gridCell, styles.headerCell, styles.entityHeaderCell)}>Entities</div>
            {Object.values(PERMISSION_ACTIONS).map((action) => (
              <div className={classNames(styles.gridCell, styles.headerCell)} key={action.id}>
                <div className={styles.actionName}>{action.name}</div>
                {action.id !== PERMISSION_ACTIONS.IMPERSONATE.id &&
                action.id !== PERMISSION_ACTIONS.EXEC_OFFLINE_TASKS_AS.id ? (
                  <ActionForAllCategoriesPermissions
                    action={action}
                    actionPermissionForAllCategoriesChanged={actionPermissionForAllCategoriesChanged}
                    allCategoriesPermissions={allCategoriesPermissions}
                    allRolePermissions={permissions}
                    availableCategoryActions={availableCategoryActions}
                    isViewOnly={isViewOnly}
                    roleId={roleId}
                  />
                ) : (
                  <InfoPopover isSimple placement="top">
                    {getInfoTooltip(action.id)}
                  </InfoPopover>
                )}
              </div>
            ))}
            {Array.isArray(permissionsToDisplay) &&
              permissionsToDisplay?.map((categoryPermissions) => (
                <>
                  <div className={classNames(styles.gridCell, styles.entityCell)}>
                    <div className={styles.entityName}>
                      <div className={styles.entityNameContainer}>
                        {PERMISSION_ENTITIES[categoryPermissions.category]?.title || categoryPermissions.category}
                        <InfoPopover isSimple placement="top">
                          {PERMISSION_ENTITIES[categoryPermissions.category]?.info}
                        </InfoPopover>
                      </div>
                    </div>
                  </div>
                  {Object.values(PERMISSION_ACTIONS).map((action) => (
                    <div className={classNames(styles.gridCell, styles.valuesCell)} key={action.id}>
                      <Permission
                        actionId={action.id}
                        allRolePermissions={permissions}
                        categoryPermissions={categoryPermissions}
                        isViewOnly={isViewOnly}
                        onPermissionChange={permissionChanged}
                        roleId={roleId}
                        userId={userId}
                      />
                    </div>
                  ))}
                </>
              ))}
          </div>
        </div>
        <div className={styles.permissionExplanationContainer}>
          <PermissionsExplanation />
        </div>
      </div>
    </div>
  );
};


export default EffectivePermissions;
